import { apiUrl, apiUrlV2 } from "../constants/apiUrl"
import formatDateForApi from "../utilities/formatDateForApi"
import rollbarLogger from "../utilities/rollbarLogger"

const initialState = {
  dealer: null,
  dealerReserveInfo: null,
  dealerReserveInfoFetching: false,
  dealerStats: null,
  dealerFetching: false,
  prospectDealerId: null,
  dealerDashboardSidePanel: null,
  repoLikelihood: null,
}

const createDealerSlice = (set, get) => ({
  ...initialState,
  fetchDealer: (dealerId, useApiUrlV2 = null) => {
    set((state) => ({ dealerFetching: true }))
    try {
      fetch(`${useApiUrlV2 ? apiUrlV2 : apiUrl}/dealers/${dealerId}`, {
        headers: get().requestHeaders,
      })
        .then((response) => response.json())
        .then((data) =>
          set((state) => ({ dealer: data.data, dealerFetching: false }))
        )
    } catch (err) {
      get().logError('Unable to fetch the dealer.', err)
    }
  },
  fetchDealerStats: () =>
    fetch(`${apiUrlV2}/dealers/full_dealer_stats`, { headers: get().requestHeaders })
      .then((response) => response.json())
      .then((data) => {
        if (data.errors) {
          set((state) => ({ dealerStats: null }))
        } else {
          set((state) => ({
            dealerStats: data.data.map((stat) => ({
              ...stat,
              principal: Number(stat.principal),
            })),
          }))
        }
  }),
  fetchBaseRateFees: () =>
    fetch(
      `${apiUrl}/dealer/base-rate-fees?date=${formatDateForApi(new Date())}`,
      { headers: get().requestHeaders }
    ),
  updateDealer: async (dealerId, attributes) => {
    try {
      const response = await fetch(`${apiUrlV2}/dealers/${dealerId}`, {
        method: "PATCH",
        headers: get().requestHeaders,
        body: JSON.stringify({
          dealer: attributes,
        }),
      })
      if (response.status === 200) {
        const data = await response.json()
        set((state) => ({ dealer: data.data }))
      }
    } catch (err) {
      get().logError('There was an error updating the dealer.', err)
    }
  },
  suspendDealer: async (dealerId, notes = null) => {
    set((state) => ({ dealerFetching: true }))
    try {
      const response = await fetch(`${apiUrlV2}/dealers/${dealerId}/suspend`, {
        method: "PATCH",
        headers: get().requestHeaders,
        body: JSON.stringify(notes ? { notes } : {}),
      })
      if (response.status === 200) {
        const data = await response.json()
        set((state) => ({ dealer: data.data }))
      }
    } catch (err) {
      get().logError('Unable to suspend the dealer.', err)
    } finally {
      set((state) => ({ dealerFetching: false }))
    }
  },
  unSuspendDealer: async (dealerId, notes = null) => {
    set((state) => ({ dealerFetching: true }))

    try {
      const response = await fetch(
        `${apiUrlV2}/dealers/${dealerId}/unsuspend`,
        {
          method: "PATCH",
          headers: get().requestHeaders,
          body: JSON.stringify(notes ? { notes } : {}),
        }
      )
      if (response.status === 200) {
        const data = await response.json()
        set((state) => ({ dealer: data.data }))
      }
    } catch (err) {
      get().logError('There was an error unsuspending the dealer.', err)
    } finally {
      set((state) => ({ dealerFetching: false }))
    }
  },
  fetchRepoLikelihood: async (dealerId, onComplete = null) => {
    try {
      const response = await fetch(`${apiUrlV2}/dealers/${dealerId}/repo_likelihood?version=v4`, {
        headers: get().requestHeaders,
      })
      const data = await response.json()
      set(() => ({ repoLikelihood: data?.data?.results ?? null }))
    } catch (err) {
      get().logError('Unable to fetch repo likelihood.', err)
    } finally {
      onComplete?.()
    }
  },
  setProspectDealerId: (id) => {
    set(() => ({ prospectDealerId: id }))
  },
  setDealerDashboardSidePanel: (panel) =>
    set(() => ({ dealerDashboardSidePanel: panel })),
  resetDealerSlice: () => set(initialState),
})

export default createDealerSlice
